import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { CheckCircle, XCircle, Clock, Loader } from 'lucide-react';
import { invitationApi } from '../services/api';
import { loginRequest, isEntraAuth } from '../auth/authConfig';
import { useAuth } from '../context/AuthContext';
import wmLogo from '../assets/logos/WM-Logo-CB-White-Header.png';

// phase: loading | valid | redirecting | completing | finishing | done | expired | accepted | error

const INVITE_STEPS = [
  { label: 'Invitation validated'              },
  { label: 'Signing you in with Microsoft'     },
  { label: 'Activating your account'           },
  { label: 'Loading your workspace'            },
];

// Map phase → how many steps are done (0-based: idx < doneCount = done, idx === doneCount = active)
const PHASE_STEP = { redirecting: 1, completing: 2, finishing: 3, done: 4 };

const AcceptInvitationPage = () => {
  const { token }        = useParams();
  const navigate         = useNavigate();
  const { instance, accounts } = useMsal();
  const { refreshUsers } = useAuth();

  const [phase, setPhase]       = useState('loading');
  const [invite, setInvite]     = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Step 1 — validate token on mount
  useEffect(() => {
    invitationApi.validate(token)
      .then(res => {
        const d = res.data;
        if (d.status === 'pending') {
          setInvite(d);
          setPhase('valid');
        } else if (d.status === 'accepted') {
          setPhase('accepted');
        } else if (d.status === 'expired') {
          setPhase('expired');
        } else {
          setPhase('error');
          setErrorMsg('Unknown invitation status.');
        }
      })
      .catch(() => {
        setPhase('error');
        setErrorMsg('Invitation not found or has been removed.');
      });
  }, [token]);

  // Step 2 — if MSAL lands us back here after login redirect, auto-complete accept
  useEffect(() => {
    if (phase === 'valid' && accounts.length > 0) {
      completeAccept();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, accounts]);

  const handleAccept = async () => {
    if (!isEntraAuth()) {
      setPhase('error');
      setErrorMsg('Authentication not configured. Contact your administrator.');
      return;
    }
    setPhase('redirecting');
    try {
      let tokenResult;
      try {
        tokenResult = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      } catch {
        // Silent failed — redirect (page reloads back here with accounts populated)
        await instance.loginRedirect({ ...loginRequest, redirectUri: window.location.href });
        return;
      }
      // Silent succeeded — skip redirect phase, go straight to completing
      setPhase('completing');
      await doAccept(tokenResult.accessToken);
    } catch (err) {
      setPhase('error');
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to accept invitation.');
    }
  };

  const completeAccept = async () => {
    setPhase('completing');
    try {
      const tokenResult = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      await doAccept(tokenResult.accessToken);
    } catch (err) {
      setPhase('error');
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to complete invitation.');
    }
  };

  const doAccept = async (accessToken) => {
    await invitationApi.accept(token, accessToken);
    setPhase('finishing');
    await new Promise(r => setTimeout(r, 500));
    await refreshUsers();
    setPhase('done');
    // Navigate to DB picker — will auto-select if only one DB
    setTimeout(() => navigate('/select-database', { replace: true }), 800);
  };

  // ── Layout wrapper ────────────────────────────────────────────────────────

  const wrap = (content) => (
    <div className="auth-gate-backdrop" style={{ background: '#1a5568' }}>
      <div className="auth-gate-card">
        {/* Header */}
        <div className="auth-gate-header">
          <img src={wmLogo} alt="Wunderlich-Malec" className="auth-gate-logo" />
          <div className="auth-gate-header-divider" />
          <div>
            <div className="auth-gate-title">DAWN</div>
            <div className="auth-gate-subtitle">DOCUMENTATION · ASSETS · WIRING · NETWORKING</div>
          </div>
        </div>
        {/* Body */}
        <div style={{ padding: '24px 32px' }}>
          {content}
        </div>
      </div>
    </div>
  );

  // ── Step-progress view (used during redirecting / completing / finishing / done) ──

  const renderSteps = () => {
    const doneCount = PHASE_STEP[phase] ?? 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {INVITE_STEPS.map((step, idx) => {
          const isDone   = idx < doneCount || phase === 'done';
          const isActive = !isDone && idx === doneCount;
          const state    = isDone ? 'done' : isActive ? 'active' : 'pending';
          return (
            <div key={idx} className={`auth-gate-step ${state}`}>
              <div className="auth-gate-step-icon">
                {isDone ? (
                  <CheckCircle size={20} color="#1a5568" />
                ) : isActive ? (
                  <Loader size={20} className="auth-gate-spinner" color="#1a5568" />
                ) : (
                  <div className="auth-gate-step-circle" />
                )}
              </div>
              <span className="auth-gate-step-label">{step.label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Phase renders ─────────────────────────────────────────────────────────

  if (phase === 'loading') return wrap(
    <div style={{ textAlign: 'center', color: '#6b7280', paddingTop: '16px' }}>
      <Loader size={28} className="auth-gate-spinner" style={{ marginBottom: '12px', color: '#1a5568' }} />
      <div style={{ fontFamily: 'Rajdhani, Arial, sans-serif', fontSize: '15px' }}>Validating invitation...</div>
    </div>
  );

  if (['redirecting', 'completing', 'finishing', 'done'].includes(phase)) return wrap(renderSteps());

  if (phase === 'accepted') return wrap(
    <div style={{ textAlign: 'center', paddingTop: '8px' }}>
      <CheckCircle size={44} color="#2563eb" style={{ marginBottom: '14px' }} />
      <div style={{ fontSize: '17px', fontWeight: 600, color: '#1f2937', marginBottom: '8px', fontFamily: 'Rajdhani, Arial, sans-serif' }}>Already accepted</div>
      <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px', fontFamily: 'Rajdhani, Arial, sans-serif' }}>This invitation has already been used.</div>
      <button onClick={() => navigate('/')} style={btnStyle('#1a5568')}>Go to DAWN</button>
    </div>
  );

  if (phase === 'expired') return wrap(
    <div style={{ textAlign: 'center', paddingTop: '8px' }}>
      <Clock size={44} color="#f59e0b" style={{ marginBottom: '14px' }} />
      <div style={{ fontSize: '17px', fontWeight: 600, color: '#1f2937', marginBottom: '8px', fontFamily: 'Rajdhani, Arial, sans-serif' }}>Invitation expired</div>
      <div style={{ fontSize: '14px', color: '#6b7280', fontFamily: 'Rajdhani, Arial, sans-serif' }}>This invitation link has expired. Contact your administrator for a new one.</div>
    </div>
  );

  if (phase === 'error') return wrap(
    <div style={{ textAlign: 'center', paddingTop: '8px' }}>
      <XCircle size={44} color="#dc2626" style={{ marginBottom: '14px' }} />
      <div style={{ fontSize: '17px', fontWeight: 600, color: '#1f2937', marginBottom: '8px', fontFamily: 'Rajdhani, Arial, sans-serif' }}>Something went wrong</div>
      <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px', fontFamily: 'Rajdhani, Arial, sans-serif' }}>{errorMsg}</div>
      <button onClick={() => navigate('/')} style={btnStyle('#6b7280')}>Back to DAWN</button>
    </div>
  );

  // phase === 'valid'
  const expiryDate = invite?.expiresAt
    ? new Date(invite.expiresAt).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  return wrap(
    <div>
      <div style={{ fontFamily: 'Rajdhani, Arial, sans-serif' }}>
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '6px' }}>
          Hi {invite?.displayName},
        </div>
        <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6, marginBottom: '18px' }}>
          You've been invited to access DAWN
          {invite?.orgName ? ` under ${invite.orgName}` : ''}.
          Sign in with your Microsoft account (<strong>{invite?.email}</strong>) to activate your access.
        </div>
      </div>
      <button onClick={handleAccept} style={btnStyle('#1a5568')}>
        Accept &amp; Sign In
      </button>
      {expiryDate && (
        <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center', marginTop: '12px', fontFamily: 'Rajdhani, Arial, sans-serif' }}>
          Invitation expires {expiryDate}
        </div>
      )}
    </div>
  );
};

const btnStyle = (bg) => ({
  display: 'block', width: '100%', padding: '12px 0',
  background: bg, color: '#fff', border: 'none', borderRadius: '6px',
  fontSize: '15px', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.5px',
  fontFamily: 'Rajdhani, Arial, sans-serif'
});

export default AcceptInvitationPage;
